import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Save, GraduationCap } from "lucide-react";
import { apiAuthGet, apiAuthSend } from "@/lib/api";
import Modal, { Field, Input, Textarea, Btn } from "./_ui";
import FileUpload from "./FileUpload";

const BLANK = { title: "", month: "", description: "", valid_from: "", valid_until: "", pdf_url: "", sort_order: 0 };

export default function EinsteigerAdmin() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = () => apiAuthGet("/admin/einsteiger").then(setItems).catch((e) => setErr(e.message));
  useEffect(() => { load(); }, []);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    const body = { ...form, sort_order: Number(form.sort_order) || 0 };
    try {
      if (editing === "new") await apiAuthSend("POST", "/admin/einsteiger", body);
      else await apiAuthSend("PUT", `/admin/einsteiger/${editing}`, body);
      setEditing(null); await load();
    } catch (ex) { setErr(ex.message); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Wirklich löschen?")) return;
    await apiAuthSend("DELETE", `/admin/einsteiger/${id}`);
    await load();
  };

  return (
    <div className="space-y-6" data-testid="admin-einsteiger">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-400 font-bold mb-1">Content</div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-white font-[Outfit]">Einsteiger-Programm</h1>
          <p className="text-slate-400 mt-1.5 text-sm">{items.length} Flyer/Programm{items.length === 1 ? "" : "e"}</p>
        </div>
        <Btn onClick={() => { setEditing("new"); setForm(BLANK); }} data-testid="einsteiger-new"><Plus className="w-4 h-4" /> Neuer Flyer</Btn>
      </div>

      {err && <div className="rounded-xl bg-red-500/10 border border-red-500/30 p-3 text-sm text-red-300">{err}</div>}

      <div className="space-y-3">
        {items.map((it) => (
          <div key={it.id} data-testid={`einsteiger-row-${it.id}`}
            className="rounded-2xl p-4 sm:p-5 bg-white/5 border border-white/10 hover:border-emerald-400/30 transition flex items-start gap-3 sm:gap-4">
            <div className="w-10 h-10 rounded-xl bg-emerald-500/15 flex items-center justify-center text-emerald-300 shrink-0">
              <GraduationCap className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-wider text-emerald-400 font-bold">{it.month}</div>
              <h3 className="font-bold text-white font-[Outfit]">{it.title}</h3>
              <p className="text-sm text-slate-400 mt-0.5 line-clamp-2">{it.description}</p>
              <div className="text-xs text-slate-500 mt-1.5">{it.valid_from} – {it.valid_until} · #{it.sort_order}</div>
            </div>
            <div className="flex flex-col gap-2 shrink-0">
              <Btn variant="secondary" onClick={() => { setEditing(it.id); setForm({ ...BLANK, ...it }); }} data-testid={`einsteiger-edit-${it.id}`}>
                <Pencil className="w-3.5 h-3.5" />
              </Btn>
              <Btn variant="danger" onClick={() => remove(it.id)} data-testid={`einsteiger-delete-${it.id}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </Btn>
            </div>
          </div>
        ))}
      </div>

      <Modal open={!!editing} onClose={() => setEditing(null)} title={editing === "new" ? "Neuer Flyer" : "Flyer bearbeiten"}>
        <form onSubmit={save} className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="einsteiger-form">
          <Field label="Titel"><Input required value={form.title} onChange={onChange("title")} placeholder="Einsteiger-Programm für 4" /></Field>
          <Field label="Monat-Label"><Input required value={form.month} onChange={onChange("month")} placeholder="April 2026" /></Field>
          <Field label="Gültig von"><Input required type="date" value={form.valid_from} onChange={onChange("valid_from")} /></Field>
          <Field label="Gültig bis"><Input required type="date" value={form.valid_until} onChange={onChange("valid_until")} /></Field>
          <div className="sm:col-span-2"><Field label="PDF (Flyer)" hint="PDF hochladen, oder URL manuell eingeben">
            <FileUpload value={form.pdf_url} onChange={(url) => setForm((f) => ({ ...f, pdf_url: url }))} testid="einsteiger-pdf" label="PDF hochladen" />
          </Field></div>
          <div className="sm:col-span-2"><Field label="Beschreibung"><Textarea rows={3} value={form.description} onChange={onChange("description")} /></Field></div>
          <Field label="Reihenfolge"><Input type="number" value={form.sort_order} onChange={onChange("sort_order")} /></Field>
          <div className="sm:col-span-2 flex justify-end gap-2 pt-2">
            <Btn type="button" variant="secondary" onClick={() => setEditing(null)}>Abbrechen</Btn>
            <Btn type="submit" disabled={busy} data-testid="einsteiger-save"><Save className="w-4 h-4" /> Speichern</Btn>
          </div>
        </form>
      </Modal>
    </div>
  );
}
